import { ChangeEvent, forwardRef, ReactNode, useRef, useState } from 'react';
import Button from '@/components/Button';
import { classNames } from '@/utils/class-names';

export interface FileInputProps {
  id: string;
  accept?: string;
  onChange: (file: File | null) => void;
  error?: string;
  value?: File | string | null;
  placeholder?: string;
  buttonText?: string;
  dragActiveText?: string;
  className?: string;
  buttonClassName?: string;
  children?: ReactNode;
}

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      id,
      accept = 'image/*',
      onChange,
      error,
      value,
      placeholder = 'Upload a file',
      buttonText = 'Browse',
      dragActiveText = 'Drop file here',
      className = '',
      buttonClassName = '',
      children,
    },
    ref,
  ) => {
    const [isDragging, setIsDragging] = useState(false);
    const innerRef = useRef<HTMLInputElement>(null);

    // Use the internal ref if no external ref is provided
    const inputRef = ref || innerRef;

    // Handle drag events
    const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        const fileType = accept.replace('*', '');

        // Check if file type is acceptable
        if (accept === '*' || file.type.includes(fileType.replace('/', '')) || accept.includes(file.type)) {
          onChange(file);
        }
      }
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) return;
      const file = e.target.files[0];
      if (file) {
        onChange(file);
      }
    };

    // Get the display text for the file
    const getDisplayText = () => {
      if (value instanceof File) {
        return value.name;
      } else if (typeof value === 'string' && value) {
        return value;
      } else if (isDragging) {
        return dragActiveText;
      } else {
        return placeholder;
      }
    };

    const handleButtonClick = () => {
      if (inputRef && 'current' in inputRef && inputRef.current) {
        inputRef.current.click();
      }
    };

    return (
      <div className="relative w-full">
        <label
          htmlFor={id}
          className={`flex min-h-[48px] w-full cursor-pointer items-center justify-between  rounded-xl
            ${
              isDragging
                ? 'bg-primary-50 border-2 border-dashed border-primary-500'
                : error
                  ? 'border border-red-500'
                  : 'border border-[#CFD8DC]'
            } 
            bg-white p-1 text-sm transition-all hover:border-dashed hover:border-primary-300 ${className}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}>
          <span className="truncate pl-4 text-sh-xxs text-[#90A4AE]">{getDisplayText()}</span>
          {children || (
            <Button
              type="button"
              onClick={handleButtonClick}
              className={classNames(buttonClassName, 'hidden sm:block')}>
              {buttonText}
            </Button>
          )}
        </label>
        <input ref={ref} type="file" id={id} accept={accept} className="hidden" onChange={handleFileChange} />
        {error && <span className="mt-1 block text-xs text-red-500">{error}</span>}
      </div>
    );
  },
);

FileInput.displayName = 'FileInput';

export default FileInput;
